import Banner from '@/components/UI/Banner';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import type { CrossDomainFinding, CrossDomainFindingGroup, SendCrossDomainTier } from '@/model/Agent.model';
import { useMemo, useState, type FC } from 'react';
import SendCellValue from './SendCellValue';
import SendDisclosureList, { type SendDisclosure } from './SendDisclosureList';
import SendFilterChips from './SendFilterChips';
import { toggled } from './SendFilterChips.utils';
import SendOmissionNotice from './SendOmissionNotice';
import SendRuleId from './SendRuleId';
import SendBlockingMark from './SendBlockingMark';
import SendFdaValidatorIds from './SendFdaValidatorIds';
import SendSeveritySource from './SendSeveritySource';
import SendSeverityBadge from './SendSeverityBadge';
import {
  bySeverityLevel,
  fdaValidatorIdsOf,
  severityChipsOf,
  severityLevelOf,
} from '@/components/Agents/SendValidator/utils/sendSeverity';
import { partitionFindings } from '@/components/Agents/SendValidator/utils/sendFindingPartition';
import { IN_VIVO_AI } from '@/components/Agents/SendValidator/utils/sendAiUse';
import { omissionOf } from '@/components/Agents/SendValidator/utils/sendOmission';
import { domainSetLabel as groupLabel, humanize, plural } from '@/components/Agents/SendValidator/utils/sendText';
import styles from './sendResults.module.scss';

interface SendCrossDomainResultsProps {
  groups: Array<CrossDomainFindingGroup>;
  /**
   * The cross-domain checks' own counts, as the server publishes them; absent from a result stored before
   * it did, whose counts are then not shown rather than worked out from the whole result less each file.
   */
  tier?: SendCrossDomainTier;
  /** The server's verdict that the cross-domain checks passed exactly, for a study in scope; else false. */
  exactPass?: boolean;
  /** Rules whose check covers only part of the rule, so their findings carry that label. */
  proxyRules?: ReadonlySet<string>;
  /**
   * How many cross-domain checks could not decide something, as the server counted them before any
   * limit. The notices on screen can be fewer, since a limit can leave one out, or more, since one
   * check can emit several.
   */
  undecidedChecks: number;
  testId?: string;
}

/**
 * Each example value with a stable key: the value and how many times it has already appeared, since a
 * bounded sample may repeat a value once it has been shortened.
 */
const keyedSamples = (samples: Array<string>): Array<{ key: string; value: string }> => {
  const seen = new Map<string, number>();
  return samples.reduce<Array<{ key: string; value: string }>>((keyed, value) => {
    const repeat = seen.get(value) ?? 0;
    seen.set(value, repeat + 1);
    keyed.push({ key: `${value}#${repeat}`, value });
    return keyed;
  }, []);
};

/** A React key for a finding within a group; findings carry no id, so combine identifying fields. */
const findingKey = (finding: CrossDomainFinding, index: number): string =>
  `${finding.category}-${finding.key_variable ?? ''}-${index}`;

/**
 * The cross-domain tier of a bundle validation: the checks' referential-integrity and consistency
 * findings, grouped by the sorted set of domains each check spanned (`DM ↔ LB`, `DM ↔ SE ↔ TA`) and
 * filtered by that set. A check's notice that it could not compare something is listed apart, drawn
 * neutrally; Benchling In Vivo AI notes are not shown here but in their own section. The tier's own counts
 * drives the headline count and the notice for anything a limit left out.
 */
const SendCrossDomainResults: FC<SendCrossDomainResultsProps> = ({
  groups,
  tier,
  exactPass = false,
  proxyRules = new Set<string>(),
  undecidedChecks,
  testId = 'invivo-send-validator__cross-domain',
}) => {
  const { checkGroups, disclosures } = useMemo(
    () =>
      groups.reduce<{ checkGroups: Array<CrossDomainFindingGroup>; disclosures: Array<SendDisclosure> }>(
        (split, group) => {
          const parts = partitionFindings(group.findings);
          if (_isNotEmpty(parts.checks)) {
            split.checkGroups.push({ ...group, findings: [...parts.checks].sort(bySeverityLevel) });
          }
          parts.disclosures.forEach((finding, index) => {
            if (_notNil(finding.disclosure)) {
              split.disclosures.push({
                key: `${groupLabel(group.domains)}-${finding.rule?.id ?? ''}-${index}`,
                reason: finding.disclosure,
                message: finding.message,
                rule: finding.rule,
                where: groupLabel(group.domains),
              });
            }
          });
          return split;
        },
        { checkGroups: [], disclosures: [] }
      ),
    [groups]
  );

  const [domainSets, setDomainSets] = useState<ReadonlySet<string>>(() => new Set());
  const [levels, setLevels] = useState<ReadonlySet<string>>(() => new Set());
  const severityChips = useMemo(
    () =>
      severityChipsOf(
        tier?.severity_counts,
        checkGroups.flatMap((group) => group.findings)
      ),
    [tier, checkGroups]
  );
  const shownGroups = checkGroups.reduce<Array<CrossDomainFindingGroup>>((shown, group) => {
    if (domainSets.size > 0 && !domainSets.has(groupLabel(group.domains))) {
      return shown;
    }
    const findings =
      levels.size === 0 ? group.findings : group.findings.filter((finding) => levels.has(severityLevelOf(finding)));
    if (_isNotEmpty(findings)) {
      shown.push({ ...group, findings });
    }
    return shown;
  }, []);

  const omission = _notNil(tier) ? omissionOf(tier.counts, tier.advisory) : undefined;
  // Only a published grading's rejections block, and none exists for SEND in M1.
  const blocking = tier?.severity_counts.reject ?? 0;
  // What the checks found, not which groups the result carries: a limit can leave every group out,
  // and a success banner above "Showing 0 of N" would read cleaner than the data.
  const found = omission?.found ?? 0;
  const aiNotes = omission?.aiNotes ?? 0;
  // Counted apart from `found`, which the server keeps to what the checks found. A check that could
  // not finish must never sit under a success banner, which would read as everything checked.
  const incomplete = undecidedChecks;
  const incompleteNote = `${plural(incomplete, 'check', 'checks')} could not be completed`;

  return (
    <div
      className="ui-card pa4"
      data-test-component="SendCrossDomainResults"
      data-test-element="container"
      data-testid={testId}
    >
      <h2 className={`${styles.sectionTitle} mb3`} data-testid="invivo-send-validator__cross-domain-title">
        Cross-domain checks
      </h2>

      {_isNil(omission) ? (
        <Banner info dismiss={false} testId="invivo-send-validator__cross-domain-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__cross-domain-headline">
            This result was stored before the cross-domain checks&apos; own counts were recorded, so none is shown.
          </p>
        </Banner>
      ) : found > 0 ? (
        <Banner
          critical={blocking > 0}
          warning={blocking === 0}
          dismiss={false}
          testId="invivo-send-validator__cross-domain-banner"
        >
          <p className="ma0 fw5" data-testid="invivo-send-validator__cross-domain-headline">
            {`${found} cross-domain ${found === 1 ? 'finding' : 'findings'}`}
            {incomplete > 0 ? ` · ${incompleteNote}` : ''}
          </p>
        </Banner>
      ) : incomplete > 0 ? (
        <Banner warning dismiss={false} testId="invivo-send-validator__cross-domain-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__cross-domain-headline">
            {`No cross-domain issues found by the validation checks — ${incompleteNote}`}
            {aiNotes > 0 ? `, and ${plural(aiNotes, `${IN_VIVO_AI} note`, `${IN_VIVO_AI} notes`)} to review` : ''}
          </p>
        </Banner>
      ) : (
        // Success only on the server's exact-pass verdict for the tier: nothing found is not the same as
        // everything checked, on a study out of scope or of unknown scope above all.
        <Banner
          success={exactPass}
          info={!exactPass}
          dismiss={false}
          testId="invivo-send-validator__cross-domain-banner"
        >
          <p className="ma0 fw5" data-testid="invivo-send-validator__cross-domain-headline">
            {aiNotes > 0
              ? `No cross-domain issues found by the validation checks — ${aiNotes} ${IN_VIVO_AI} ${aiNotes === 1 ? 'note' : 'notes'} to review`
              : exactPass
                ? 'No cross-domain issues found across the bundle'
                : 'No cross-domain issues found in the checks that ran'}
          </p>
        </Banner>
      )}

      {/* AI notes are counted in their own section; this one announces only the checks' own omissions. */}
      {_notNil(omission) && (
        <SendOmissionNotice
          omission={{ ...omission, aiNotes: 0, aiNotesNotShown: 0 }}
          testId="invivo-send-validator__cross-domain-omission"
        />
      )}

      {_isNotEmpty(checkGroups) && _isNotEmpty(severityChips) && (
        <div className="mt3">
          <SendFilterChips
            label="Filter cross-domain findings by severity"
            options={severityChips}
            selected={levels}
            onToggle={(key) => setLevels((current) => toggled(current, key))}
            testId="invivo-send-validator__cross-domain-severity-filter"
          />
        </div>
      )}
      {_isNotEmpty(checkGroups) && !_isNotEmpty(shownGroups) && (
        <p className="f6 dark-gray ma0 mt2" data-test-element="cross-domain-filter-empty">
          None of the cross-domain findings shown here matches the selected filters.
        </p>
      )}

      {checkGroups.length > 1 && (
        <div className="mt3">
          <SendFilterChips
            label="Filter cross-domain findings by the domains they span"
            options={checkGroups.map((group) => ({ key: groupLabel(group.domains), label: groupLabel(group.domains) }))}
            selected={domainSets}
            onToggle={(key) => setDomainSets((current) => toggled(current, key))}
            testId="invivo-send-validator__domain-set-filter"
          />
        </div>
      )}

      {shownGroups.map((group) => (
        <section
          key={groupLabel(group.domains)}
          className={styles.group}
          data-test-element="cross-domain-group"
          data-test-key={groupLabel(group.domains)}
          data-testid="invivo-send-validator__cross-domain-group"
        >
          <h3 className={styles.groupHeader} data-test-element="cross-domain-group-label">
            {groupLabel(group.domains)}
          </h3>
          <ul className={styles.groupFindings}>
            {group.findings.map((finding, index) => (
              <li
                key={findingKey(finding, index)}
                className={styles.groupFinding}
                data-test-element="cross-domain-finding"
              >
                <div className={styles.findingHeadline}>
                  <SendSeverityBadge level={severityLevelOf(finding)} />
                  <SendBlockingMark finding={finding} />
                  <SendSeveritySource finding={finding} />
                  {_notNil(finding.rule) && (
                    <SendRuleId
                      id={finding.rule.id}
                      publisher={finding.rule.publisher}
                      partial={proxyRules.has(finding.rule.id)}
                    />
                  )}
                  <span className="f6 fw6 near-black" data-test-element="cross-domain-finding-meta">
                    {humanize(finding.category)}
                    {_notNil(finding.key_variable) ? ` · ${finding.key_variable}` : ''}
                    {_notNil(finding.occurrence_count)
                      ? ` · ${plural(finding.occurrence_count, 'occurrence', 'occurrences')}`
                      : ''}
                  </span>
                </div>
                <p className={styles.findingMessage}>{finding.message}</p>
                <SendFdaValidatorIds ids={fdaValidatorIdsOf(finding)} />
                {_notNil(finding.sample_values) && _isNotEmpty(finding.sample_values) && (
                  <div className={styles.samples} data-test-element="cross-domain-finding-samples">
                    {keyedSamples(finding.sample_values).map(({ key, value }) => (
                      <span key={key} className={styles.sample} data-test-element="cross-domain-finding-sample">
                        <SendCellValue value={value} />
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}

      <SendDisclosureList
        disclosures={disclosures}
        undecidedChecks={undecidedChecks}
        testId="invivo-send-validator__cross-domain-disclosures"
      />
    </div>
  );
};

export default SendCrossDomainResults;
